import React, { useEffect, useState } from "react";
import { Container, Grid, Typography, Box, Card, Button } from "@mui/material";
import { Helmet } from "react-helmet";
import Top10QnAList from "../components/dashboard/Top10QnAList";
import ChatUsers from "../components/dashboard/ChatUsers";
import ChartUserFeedback from "../components/dashboard/ChartUserFeedback";
import ChatDetails from "../components/dashboard/ChatDetails";
import ModernCard from "../components/ModernCard";
import ModernLoader from "../components/ModernLoader";
import dashboardService from "../services/dashboard.service";
import knowledgebaseAgentService from "../services/knowledgebase-agent.service";
import Psychology from "@mui/icons-material/Psychology";
import { useNavigate } from "react-router-dom";

const Activities = () => {
  const [chatDetails, setChatDetails] = useState([]);
  const [chatUsers, setChatUsers] = useState([]);
  const [kbAgent, setKbAgent] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchChatDetailsAndUsers = async () => {
      try {
        setLoading(true);
        const [chats, users, kbAgent] = await Promise.all([
          dashboardService.getAllChatDetail(),
          dashboardService.getAllChatDetail(),
          knowledgebaseAgentService.getAllKnowledgeBases(),
        ]);
        setChatDetails(chats);
        setChatUsers(users);
        setKbAgent(kbAgent);
      } catch (error) {
        console.error("Error fetching chat details and users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChatDetailsAndUsers();
  }, []);

  return (
    <>
      <Helmet>
        <title>Activities | Sitepilot</title>
        <meta
          name="description"
          content="View chat analytics, user feedback, and engagement trends"
        />
      </Helmet>

      <Container
        component="main"
        maxWidth="lg"
        sx={{
          position: "relative",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 4 },
        }}
      >
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: "-0.02em",
              mb: 1.5,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Activities
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, fontSize: { xs: "1rem", md: "1.25rem" } }}
          >
            Chat analytics, user engagement, and site activity insights
          </Typography>
        </Box>

        {loading ? (
          <ModernLoader message="Loading activities..." />
        ) : kbAgent.length === 0 ? (
          /**
           * ---------------------------------------
           * EMPTY STATE (when no KB Agents exist)
           * ---------------------------------------
           */
          <Box sx={{ maxWidth: 600, mx: "auto", mt: 6 }}>
            <Card
              sx={{
                textAlign: "center",
                p: 6,
                border: "2px dashed rgba(102, 126, 234, 0.3)",
                borderRadius: 4,
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.15)",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "rgba(102, 126, 234, 0.5)",
                  boxShadow: "0 24px 48px -12px rgba(102, 126, 234, 0.2)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  border: "2px solid rgba(102, 126, 234, 0.2)",
                }}
              >
                <Psychology
                  sx={{
                    fontSize: 40,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                No Agents Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Complete onboarding to create your first AI chat agent.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/app/agent/add")}
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                    boxShadow: "0 12px 32px rgba(102, 126, 234, 0.4)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Start Onboarding
              </Button>
            </Card>
          </Box>
        ) : (
          /**
           * ---------------------------------------
           * DASHBOARD CONTENT (when agents exist)
           * ---------------------------------------
           */
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ModernCard gradient glow>
                  <ChatUsers chatUsers={chatUsers} />
                </ModernCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <ModernCard gradient glow>
                  <ChatDetails chatDetails={chatDetails} />
                </ModernCard>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mt: 3 }}>
              <Grid item xs={12} md={4}>
                <ModernCard gradient>
                  <ChartUserFeedback chatDetails={chatDetails} />
                </ModernCard>
              </Grid>
              <Grid item xs={12} md={8}>
                <ModernCard gradient>
                  <Top10QnAList chatDetails={chatDetails} />
                </ModernCard>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </>
  );
};

export default Activities;
